import { useState } from 'react';
import { Instagram, Youtube } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useDjangoData } from '@/contexts/DjangoContext';
import { useCSRF } from '@/contexts/CSRFContext';

const PreLaunchPage = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { toast } = useToast();
  const { data } = useDjangoData();
  const { getToken } = useCSRF();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address.",
        variant: "destructive"
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Get CSRF token using CSRF context
      const csrfToken = await getToken();
      
      const response = await fetch('/api/early-signup/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
        body: JSON.stringify({ email: email.toLowerCase().trim() })
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 409) {
          toast({
            title: "Already signed up",
            description: "This email is already registered. Thank you for your interest!",
            variant: "default"
          });
          setIsSubmitted(true);
        } else {
          throw new Error(data.error || 'Something went wrong');
        }
      } else {
        setIsSubmitted(true);
        toast({
          title: "Success!",
          description: "You're in. We'll let you know when the adventure begins.",
          variant: "default"
        });
      }
    } catch (error) {
      console.error('Error saving email:', error);
      toast({
        title: "Something went wrong",
        description: "Please try again later.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-adv-sand flex flex-col">
      <main className="flex-grow flex flex-col items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <div className="w-full max-w-md space-y-12 text-center">
          {/* Logo - Doubled size */}
          <div className="flex justify-center">
            <img 
              src={data.urls?.logo} 
              alt="Exodus ADV Logo" 
              className="h-56 sm:h-72"
            />
          </div>
          
          {/* Headline and Mission Statement */}
          <div className="space-y-4">
            <h1 className="text-3xl sm:text-4xl font-bold text-adv-dark">
              The road out begins soon.
            </h1>
            <p className="text-lg text-adv-darkGray max-w-sm mx-auto">
              ExodusADV is your new home for adventure motorcycle rallies, ride networks, and community-powered exploration.
            </p>
          </div>
          
          {/* Email Signup Form */}
          {!isSubmitted ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-adv-darkGray font-medium">
                Enter your email to be the first to know when we launch.
              </p>
              <div className="space-y-2">
                <Label htmlFor="email" className="sr-only">Email</Label>
                <div className="flex gap-2 max-w-sm mx-auto">
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="border-adv-green/50 focus:border-adv-green"
                  />
                  <Button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="bg-adv-orange hover:bg-adv-orange/90 text-white"
                  >
                    Notify Me
                  </Button>
                </div>
              </div>
            </form>
          ) : (
            <div className="py-8 text-center">
              <div className="inline-flex items-center justify-center bg-adv-green/10 text-adv-green rounded-full p-3 mb-4">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 13L9 17L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-adv-dark">
                You're in. We'll let you know when the adventure begins.
              </h3>
            </div>
          )}
          
          {/* Social Links */}
          <div className="pt-6 flex items-center justify-center space-x-4">
            <a 
              href="https://www.instagram.com/ExodusADV" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center text-adv-darkGray hover:text-adv-orange transition-colors"
            >
              <Instagram className="mr-2" />
              <span>@ExodusADV</span>
            </a>
            <a 
              href="https://www.youtube.com/@Exodusadv" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center text-adv-darkGray hover:text-adv-orange transition-colors"
            >
              <Youtube className="mr-2" />
              <span>@ExodusADV</span>
            </a>
          </div>
        </div>
      </main>
      
      {/* Footer */}
      <footer className="py-6 text-center text-adv-darkGray text-sm">
        <p>© {new Date().getFullYear()} ExodusADV. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default PreLaunchPage;
